import { multi, withDependencies, optional, named } from '@wix/thunderbolt-ioc'
import type { IRendererPropsExtender, ServiceProvider, Experiments } from '@wix/thunderbolt-symbols'
import {
	SiteServicesManagerSymbol,
	ServiceProviderSymbol,
	ServiceProviderAdapterSymbol,
	SignalsServiceProviderSymbol,
	ExperimentsSymbol,
	SiteFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { createServicesManager, createServicesMap } from '@wix/services-manager'
import { ServicesManagerProvider } from '@wix/services-manager-react'
import type { ServicesManager } from '@wix/services-manager/types'
import { name } from './symbols'
import type { ServiceRegistrarSiteConfig } from './types'

export const SiteServiceManager = withDependencies<ServicesManager>(
	[multi(ServiceProviderSymbol), SignalsServiceProviderSymbol, ExperimentsSymbol, named(SiteFeatureConfigSymbol, name)],
	(
		servicesProviders: Array<ServiceProvider>,
		signalsServiceProvider: ServiceProvider,
		experiments: Experiments,
		siteConfig: ServiceRegistrarSiteConfig
	): ServicesManager => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = !!experiments['specs.thunderbolt.servicesInfra'] || isBuilder
		const services = [signalsServiceProvider]
		return createServicesManager(createServicesMap(isServicesInfraOpen ? [...servicesProviders, ...services] : []))
	}
)

export const ServiceRegistrarPropsExtender = withDependencies(
	[SiteServicesManagerSymbol],
	(servicesManager: ServicesManager): IRendererPropsExtender => {
		return {
			async extendRendererProps() {
				return { servicesManager, ServicesManagerProvider }
			},
		}
	}
)

/**
 * Adapter for services that depend on features which themselves depend on services.
 * Use ServiceProviderAdapterSymbol instead of ServiceProviderSymbol to register services
 * that would otherwise cause circular dependencies in the IoC container.
 *
 * This adapter collects service providers registered via ServiceProviderAdapterSymbol
 * and adds them to the ServicesManager after its initial creation, breaking the dependency cycle.
 */
export const SiteServicesManagerAdapter = withDependencies<ServicesManager>(
	[
		multi(ServiceProviderAdapterSymbol),
		SiteServicesManagerSymbol,
		ExperimentsSymbol,
		named(SiteFeatureConfigSymbol, name),
	],
	(
		serviceAdapters: Array<ServiceProvider>,
		servicesManager: ServicesManager,
		experiments: Experiments,
		siteConfig: ServiceRegistrarSiteConfig
	): ServicesManager => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfraOpen = !!experiments['specs.thunderbolt.servicesInfra'] || isBuilder
		servicesManager.addServices(createServicesMap(isServicesInfraOpen ? serviceAdapters : []))
		return servicesManager
	}
)
